import {
    Sun, SquareAsterisk, Calculator, Hand, Smile, Moon, Heart, HelpCircle, Star, Sparkles,
    Settings, Infinity, CalendarCheck, BookOpen, Compass, Brain, Shield, Target, Zap, Lock, Languages, ChevronDown, Check, ArrowRight, HeartHandshake
} from 'lucide-react';
import {
    VedicIcon, CompatibilityIcon, PrashanIcon, PastLifeIcon, PanchangIcon,
    WesternIcon, ChineseIcon, KPIcon, LalKitabIcon, TarotIcon, PalmistryIcon,
    FaceIcon, NumerologyIcon, DreamIcon, PsychologistIcon, AstrocartographyIcon,
} from '@components/icons/ServiceIcons';
import { pricing } from '@config/pricing';

export const IconMap: { [key: string]: any } = {
    Sun, SquareAsterisk, Calculator, Hand, Smile, Moon, Heart, HelpCircle, Star, Sparkles,
    Settings, Infinity, CalendarCheck, BookOpen, Compass, Brain, Shield, Target, Zap, Lock, Languages, ChevronDown, Check, ArrowRight, HeartHandshake,
    VedicIcon, CompatibilityIcon, PrashanIcon, PastLifeIcon, PanchangIcon,
    WesternIcon, ChineseIcon, KPIcon, LalKitabIcon, TarotIcon, PalmistryIcon,
    FaceIcon, NumerologyIcon, DreamIcon, PsychologistIcon, AstrocartographyIcon,
};

// Service data with icons as string names for easier management
export const servicesData = [
    {
        iconName: 'VedicIcon',
        name: "Vedic Astrology (Jyotish)",
        subtitle: "Birth chart, dashas, predictions",
        desc: "Complete chart reading with dashas and yogas.",
        features: ["Kundli", "Dashas", "Yogas"],
        gradient: "from-purple-500 to-pink-500"
    },
    {
        iconName: 'TarotIcon',
        name: "Tarot Readings",
        subtitle: "Spreads, clarity, guidance",
        desc: "Clarity through powerful spreads and intuitive guidance.",
        features: ["Single Card", "Three-Card", "Celtic Cross"],
        gradient: "from-yellow-500 to-orange-500"
    },
    {
        iconName: 'NumerologyIcon',
        name: "Numerology Analysis",
        subtitle: "Life path, destiny, name",
        desc: "Decode numbers that shape personality and destiny.",
        features: ["Life Path", "Destiny", "Name"],
        gradient: "from-indigo-500 to-purple-500"
    },
    {
        iconName: 'PalmistryIcon',
        name: "Palm Reading",
        subtitle: "Lines, mounts, markings",
        desc: "Traditional Hastrekha insights for life direction.",
        features: ["Lines", "Mounts", "Signs"],
        gradient: "from-green-500 to-teal-500"
    },
    {
        iconName: 'FaceIcon',
        name: "Face Reading",
        subtitle: "Traits, strengths, compatibility",
        desc: "Samudrik Shastra for traits and compatibility.",
        features: ["Traits", "Career", "Love"],
        gradient: "from-blue-500 to-cyan-500"
    },
    {
        iconName: 'DreamIcon',
        name: "Dream Interpretation",
        subtitle: "Symbols, messages, guidance",
        desc: "Decode symbolic messages from the subconscious.",
        features: ["Symbols", "Themes", "Guidance"],
        gradient: "from-pink-500 to-rose-500"
    },
    {
        iconName: 'CompatibilityIcon',
        name: "Compatibility Analysis",
        subtitle: "Guna Milan, harmony, guidance",
        desc: "Assess compatibility with time-tested matching methods.",
        features: ["Guna Milan", "Synastry", "Remedies"],
        gradient: "from-red-500 to-pink-500"
    },
    {
        iconName: 'PrashanIcon',
        name: "Prashan Kundli (Horary)",
        subtitle: "Yes/No, timing, decisions",
        desc: "Moment-based answers for specific questions.",
        features: ["Yes/No", "Timing", "Decisions"],
        gradient: "from-orange-500 to-red-500"
    },
    {
        iconName: 'WesternIcon',
        name: "Western Astrology",
        subtitle: "Transits, aspects, growth",
        desc: "Modern chart work focused on growth.",
        features: ["Transits", "Aspects", "Progressions"],
        gradient: "from-blue-500 to-indigo-500"
    },
    {
        iconName: 'ChineseIcon',
        name: "Chinese Astrology",
        subtitle: "Zodiac, elements, harmony",
        desc: "Eastern wisdom grounded in cycles and elements.",
        features: ["Zodiac", "Elements", "Feng Shui"],
        gradient: "from-red-500 to-orange-500"
    },
    {
        iconName: 'KPIcon',
        name: "KP System",
        subtitle: "Sub-lords, timing, cusps",
        desc: "Highly precise predictions with sub-lord focus.",
        features: ["Sub-lords", "Timing", "Cusps"],
        gradient: "from-teal-500 to-cyan-500"
    },
    {
        iconName: 'PastLifeIcon',
        name: "Past Life Reading",
        subtitle: "Karmic patterns, soul journey, D60 chart",
        desc: "Deep karmic analysis revealing past life patterns and soul evolution journey through D60 chart.",
        features: ["Karmic Patterns", "Soul Journey", "D60 Chart"],
        gradient: "from-purple-500 to-indigo-500"
    },
    {
        iconName: 'PanchangIcon',
        name: "AI Panchang",
        subtitle: "Tithi, Nakshatra, Yoga, Karana, timings",
        desc: "AI-powered Hindu calendar analysis with tithi, nakshatra, yoga, karana, and auspicious timings.",
        features: ["Tithi", "Nakshatra", "Yoga"],
        gradient: "from-green-500 to-teal-500"
    },
    {
        iconName: 'LalKitabIcon',
        name: "Lal Kitab",
        subtitle: "Karmic debt, dasha, remedies",
        desc: "Lal Kitab astrology with karmic debt analysis, 35-year dasha cycle, and practical remedies for changing destiny.",
        features: ["Karmic Debt", "35-Year Dasha", "Remedies"],
        gradient: "from-red-500 to-orange-500"
    },
    {
        iconName: 'PsychologistIcon',
        name: "AI Psychologist",
        subtitle: "Mental wellness, CBT, support",
        desc: "Your compassionate AI companion for mental wellness - vent freely, manage anxiety, and build confidence.",
        features: ["Anxiety Help", "CBT Support", "Self-Esteem"],
        gradient: "from-teal-500 to-emerald-500"
    }
];

export const guidanceTopicsData = [
    {
        title: "Career",
        iconName: 'Compass',
        questions: [
            "When will I get a job?",
            "Which career path is best for me?",
            "Should I do business or job?"
        ]
    },
    {
        title: "Love & Marriage",
        iconName: 'Heart',
        questions: [
            "When will I get married?",
            "Will it be a love or arranged marriage?",
            "How is my partner's nature?"
        ]
    },
    {
        title: "Education",
        iconName: 'Brain',
        questions: [
            "Will I clear the exam?",
            "Is higher study abroad possible?",
            "Which subject should I choose?"
        ]
    },
    {
        title: "Health & Wealth",
        iconName: 'Shield',
        questions: [
            "When will my health improve?",
            "How is my financial future?",
            "Is there any property gain?"
        ]
    }
];

export const destinationFeaturesData = [
    {
        iconName: 'Target',
        title: "Accurate Predictions",
        desc: "We use advanced AI algorithms to provide the most accurate astrology predictions.",
        dotColor: "bg-yellow-400",
        iconBg: "bg-gradient-to-br from-purple-500/20 to-pink-500/20"
    },
    {
        iconName: 'Zap',
        title: "Instant Consultation",
        desc: "No need to wait! Get your astrology consultation instantly.",
        dotColor: "bg-pink-400",
        iconBg: "bg-gradient-to-br from-orange-500/20 to-pink-500/20"
    },
    {
        iconName: 'Lock',
        title: "Confidential",
        desc: "All your data and consultations are kept strictly confidential.",
        dotColor: "bg-blue-400",
        iconBg: "bg-gradient-to-br from-indigo-500/20 to-purple-500/20"
    },
    {
        iconName: 'Languages',
        title: "10+ Language Support",
        desc: "Chat in your native language with support for 12 major languages worldwide.",
        dotColor: "bg-green-400",
        iconBg: "bg-gradient-to-br from-cyan-500/20 to-blue-500/20"
    }
];

export const testimonialsData = [
    {
        name: "Priya Sharma",
        role: "Entrepreneur",
        text: "BhriguGPT's career predictions were incredibly accurate. It helped me make a crucial business decision at the right time.",
        rating: 5
    },
    {
        name: "Rahul Verma",
        role: "Software Engineer",
        text: "The relationship compatibility analysis provided deep insights into my marriage. Highly recommended for spiritual guidance.",
        rating: 5
    },
    {
        name: "Ananya Iyer",
        role: "Student",
        text: "I was confused about my higher studies. The AI's education guidance was spot on and very detailed.",
        rating: 5
    },
    {
        name: "Vikram Singh",
        role: "Marketing Manager",
        text: "The daily Panchang and Muhurat timings are very helpful for my daily activities. Truly a modern spiritual companion.",
        rating: 5
    },
    {
        name: "Sneha Gupta",
        role: "Designer",
        text: "I love the face reading and dream analysis features. They are so unique and surprisingly accurate!",
        rating: 5
    },
    {
        name: "Amit Patel",
        role: "Doctor",
        text: "A perfect blend of ancient wisdom and modern technology. BhriguGPT is my go-to for all astrological queries.",
        rating: 5
    }
];

export const faqsData = [
    {
        q: "What is BhriguGPT and how does it work?",
        a: "BhriguGPT is a comprehensive AI-powered spiritual guidance platform that combines ancient wisdom traditions with modern technology. We offer Vedic astrology, Western & Chinese astrology, KP System, tarot readings, numerology, palm reading, face reading, dream analysis, compatibility analysis, horary astrology, and more. Simply chat with our AI like you would with ChatGPT, ask questions about your life, relationships, career, or spiritual journey, and receive detailed insights and guidance based on traditional wisdom principles from Maharishi Bhrigu and other ancient spiritual sciences."
    },
    {
        q: `How does the ${pricing.currency}${pricing.signupReward} free credit work?`,
        a: `When you sign up for BhriguGPT, you automatically receive ${pricing.currency}${pricing.signupReward} in free credits added to your wallet. Each message costs ${pricing.currency}${pricing.quick}, so you can send approximately ${pricing.freeQuestions} messages for free to explore the platform. No credit card is required for signup, and your free credits never expire. It's our way of letting you experience the wisdom of Vedic astrology risk-free!`
    },
    {
        q: "What kind of questions can I ask?",
        a: "questions_list"
    },
    {
        q: "Do I need to provide my birth details?",
        a: "birth_details_list"
    },
    {
        q: "How much does each message cost?",
        a: `Each message starts from ${pricing.currency}${pricing.quick}, regardless of which spiritual guidance service you choose (Vedic astrology, Western & Chinese astrology, KP System, tarot readings, numerology, palm reading, face reading, dream analysis, compatibility analysis, or horary astrology). This is a simple pay-per-use model with no monthly subscriptions or hidden fees. You only pay for the divine guidance you actually receive. Credits can be added to your wallet anytime, starting from as low as ${pricing.currency}50, and your credits never expire.`
    },
    {
        q: "Is BhriguGPT available in other languages?",
        a: "BhriguGPT supports 12 languages including English, Hindi, Tamil, Telugu, Gujarati, Marathi, Bangla, Spanish, Portuguese, French, Chinese, and Arabic. You can chat in any of these languages and receive responses in the same language, making ancient Vedic wisdom accessible to everyone in their preferred language."
    },
    {
        q: "How accurate are the spiritual guidance insights?",
        a: "accuracy_list"
    },
    {
        q: "Can I get a refund if I'm not satisfied?",
        a: "refund_policy"
    }
];
